import { useParams } from 'react-router-dom';
import { Row, Col, Card, Input, Select, Button, Space, Spin, DatePicker, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useArticleEditor } from '../hooks/useArticleEditor';
import Tiptap from '../components/editor/Tiptap';
import SeoPanel from '../components/SeoPanel';
import RelatedSection from '../components/RelatedSection';
import PublishActions from '../components/PublishActions';
import PageSettingsCard from './article-edit/PageSettingsCard';
import CoverCard from './article-edit/CoverCard';
import CategoryCard from './article-edit/CategoryCard';

const STATUS_OPTS = [
  { value: 'draft', label: '草稿' },
  { value: 'in_review', label: '審稿中' },
  { value: 'scheduled', label: '排程刊登' },
  { value: 'published', label: '發佈' },
];

export default function ArticleEditPage() {
  const { id = '' } = useParams();
  const { state, patch, save, loading, legacyHtml } = useArticleEditor(id);
  const isPage = state.kind === 'page';
  const pages = useQuery({
    queryKey: ['articles', { kind: 'page' }],
    queryFn: () => api.listArticles({ kind: 'page', pageSize: '200' }),
    enabled: isPage,
  });
  if (loading) return <Spin size="large" className="block py-24 text-center" />;

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="middle" className="w-full">
            <Input
              size="large"
              placeholder={isPage ? '頁面標題' : '文章標題'}
              value={state.title}
              onChange={(e) => patch({ title: e.target.value })}
              prefix={<Tag color={isPage ? 'purple' : 'blue'}>{isPage ? '頁面' : '文章'}</Tag>}
            />
            <Tiptap
              key={id}
              initialJson={state.bodyJson}
              initialHtml={legacyHtml}
              onChange={(json) => patch({ bodyJson: json })}
            />
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size="middle" className="w-full">
            <Card title="發佈設定">
              <Space direction="vertical" className="w-full">
                <Select className="w-full" value={state.status} options={STATUS_OPTS} onChange={(v) => patch({ status: v })} />
                {state.status === 'scheduled' && (
                  <DatePicker
                    showTime
                    className="w-full"
                    placeholder="排程刊登時間（Asia/Taipei）"
                    value={state.scheduledAt ? dayjs(state.scheduledAt) : null}
                    onChange={(d) => patch({ scheduledAt: d ? d.valueOf() : null })}
                  />
                )}
                <Button type="primary" block loading={save.isPending} onClick={() => save.mutate()}>
                  儲存
                </Button>
                <PublishActions id={id} status={state.status} title={state.title} bodyJson={state.bodyJson} />
              </Space>
            </Card>

            <CoverCard
              coverUrl={state.coverUrl}
              onSet={(coverMediaId, coverUrl) => patch({ coverMediaId, coverUrl })}
              onRemove={() => patch({ coverMediaId: null, coverUrl: null })}
            />

            {isPage ? (
              <PageSettingsCard
                pages={pages.data?.items ?? []}
                currentId={id}
                parentId={state.parentId}
                menuOrder={state.menuOrder}
                onParent={(parentId) => patch({ parentId })}
                onMenuOrder={(menuOrder) => patch({ menuOrder })}
              />
            ) : (
              <CategoryCard value={state.categoryIds} onChange={(v) => patch({ categoryIds: v })} />
            )}

            <SeoPanel value={state.seo} onChange={(seo) => patch({ seo })} fallbackTitle={state.title} coverUrl={state.coverUrl} />
          </Space>
        </Col>
      </Row>

      {!isPage && (
        <RelatedSection
          excludeId={id}
          value={state.relatedIds}
          onChange={(ids) => patch({ relatedIds: ids })}
        />
      )}
    </>
  );
}
